import { useEffect, useMemo, useState } from "react";
import { Box, Card, CardActions, CardContent, CardMedia, IconButton, Stack, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import CloseIcon from '@mui/icons-material/Close';
import { useTracking } from "../../Tracking/TrackingContext";
import { dismissContentCard, logUserHasSeenContentCard } from "../../Tracking/Braze/InitBraze";
import { ClassicCard } from "@braze/web-sdk";

interface BrazeContentCardProps {
    customPageFeed: string;
}

const BrazeContentCard: React.FunctionComponent<BrazeContentCardProps> = ({ customPageFeed }: BrazeContentCardProps) => {
    const { contentCards, setContentCards } = useTracking();
    const filteredCards = useMemo(() => {
        return contentCards?.filter((card) => card.extras['show_on'] ? card.extras['show_on'].includes(customPageFeed) : false) ?? []
    }, [contentCards, customPageFeed]);
    const [showContentCard, setShowContentCard] = useState(false);

    const onDismissContentCard = (cardToDismiss: ClassicCard) => {
        dismissContentCard(cardToDismiss);
        setContentCards(contentCards?.filter((card) => card.id != cardToDismiss.id))
    };

    useEffect(() => {
        if (filteredCards.length > 0) {
            setShowContentCard(true);
            logUserHasSeenContentCard(filteredCards);
        }
        else setShowContentCard(false);
    }, [filteredCards]);

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 16,
                right: 16,
                width: 231,
                bgcolor: '#fff',
                boxShadow: 3,
                borderRadius: "0.8rem",
                p: 0,
                zIndex: 1300, // higher than default app bar
                display: showContentCard ? "block" : "none",
                backgroundColor: "#F5F5F5"
            }}
        >
            <Box sx={{ height: "4.7rem", borderRadius: "0.8rem", backgroundColor: "#FFFFFF" }}>
                <IconButton
                    sx={{ position: 'absolute', top: 12, right: 8 }}
                    size="small"
                    onClick={() => setShowContentCard(false)}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <Box maxHeight={'calc(100vh - 90px)'} borderRadius={'0.8rem'} overflow={"auto"}>
                <Stack spacing={2} padding={'1.6rem'} bgcolor={'#F5F5F5'} borderRadius={"0.8rem"}>
                    {
                        filteredCards?.map(card =>
                            <Card sx={{ borderRadius: "0.8rem" }} key={card.id}>
                                <Stack direction={"row"} justifyContent={"end"} height={"2.6rem"} paddingRight={"0.4rem"}>
                                    <IconButton
                                        onClick={() => onDismissContentCard(card)}
                                        sx={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                                            '&:hover': { backgroundColor: 'rgba(255, 255, 255, 1)' },
                                            zIndex: 2,
                                        }}
                                        size="small"
                                    >
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </Stack>

                                {card.imageUrl && (
                                    <Box sx={{ paddingRight: "0.8rem", paddingLeft: "0.8rem", marginBottom: "1.6rem" }}>
                                        <CardMedia
                                            component="img"
                                            height="140"
                                            image={card.imageUrl}
                                            alt="Card image"
                                            sx={{ borderRadius: "8px" }}
                                        />
                                    </Box>
                                )}

                                <CardContent sx={{ padding: "0 1.6rem 1.6rem 1.6rem" }}>
                                    <Typography gutterBottom sx={{ fontSize: "1.5rem", lineHeight: "2.3rem", fontWeight: 400 }}>
                                        {card.title}
                                    </Typography>
                                    <Typography sx={{ fontSize: "1.3rem", lineHeight: "1.7rem", fontWeight: 300 }}>{card.description}</Typography>
                                </CardContent>
                                {
                                    card.url && card.linkText &&
                                    <CardActions>
                                        <Link to={card.url} style={{ textDecoration: 'none', fontSize: '1.3rem', color: "#008fd5" }}>{card.linkText}</Link>
                                    </CardActions>
                                }
                            </Card>
                        )
                    }
                </Stack>
            </Box>
        </Box>
    );
};

export default BrazeContentCard;